import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { Calendar, ChevronRight, CreditCard, FileText, Headphones, Rocket, Search, Trophy, Users } from 'lucide-react';

const helpCategories = [
  { icon: Rocket, label: 'Débuter', articles: 12, color: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
  {
    icon: Trophy,
    label: 'Concours',
    articles: 18,
    color: 'bg-emerald-50 text-emerald-600',
    border: 'border-emerald-100',
  },
  { icon: Users, label: 'Clubs', articles: 14, color: 'bg-purple-50 text-purple-600', border: 'border-purple-100' },
  {
    icon: Calendar,
    label: 'Organisateurs',
    articles: 22,
    color: 'bg-amber-50 text-amber-600',
    border: 'border-amber-100',
  },
  { icon: CreditCard, label: 'Paiements', articles: 10, color: 'bg-rose-50 text-rose-600', border: 'border-rose-100' },
  { icon: FileText, label: 'FFPJP', articles: 8, color: 'bg-cyan-50 text-cyan-600', border: 'border-cyan-100' },
];

const faqSections: { title: string; questions: { q: string; a: React.ReactNode }[] }[] = [
  {
    title: 'Débuter',
    questions: [
      {
        q: 'Comment créer un compte sur BoulesConnect ?',
        a: "Rendez-vous sur la page d'inscription, choisissez votre profil (Joueur, Club ou Organisateur) et validez votre email. Votre compte sera actif immédiatement pour les fonctionnalités gratuites.",
      },
      {
        q: 'Puis-je être à la fois joueur et administrateur (club/organisateur) ?',
        a: 'Pour des raisons de sécurité et de conformité, un compte BoulesConnect correspond à un seul rôle (Joueur OU Admin Club OU Organisateur). Si vous souhaitez à la fois administrer et jouer, il faut créer deux comptes distincts avec deux adresses email.',
      },
      {
        q: "L'application est-elle disponible sur mobile ?",
        a: "BoulesConnect est une 'Progressive Web App'. Elle s'adapte à tous les écrans et vous pouvez l'installer sur votre écran d'accueil mobile via le menu de votre navigateur pour une expérience fluide comme une application native.",
      },
    ],
  },
  {
    title: 'Concours & Inscriptions',
    questions: [
      {
        q: "Comment m'inscrire à un concours ?",
        a: 'Utilisez la recherche géolocalisée pour trouver un concours. Cliquez sur "S\'inscrire", remplissez les noms des joueurs (ou numéros de licence) et procédez au paiement si nécessaire. Un email de confirmation vous sera envoyé.',
      },
      {
        q: 'Puis-je modifier mon équipe après validation ?',
        a: "Oui, tant que les inscriptions sont ouvertes, vous pouvez modifier la composition de votre équipe depuis votre tableau de bord Joueur. Une fois les inscriptions closes, seul l'organisateur peut effectuer des changements.",
      },
      {
        q: 'Où trouver les résultats et le podium ?',
        a: "Les résultats sont publiés par l'organisateur après le concours. Vous pouvez les consulter sur la page publique du concours (onglet Résultats) ou dans votre historique personnel.",
      },
      {
        q: 'Pourquoi mon inscription est refusée sur un concours restreint ?',
        a: "Certains concours sont limités à des joueurs éligibles (membres d'un club, clubs affiliés, licence renseignée, etc.). Si vous n'êtes pas éligible, vérifiez votre profil (informations, numéro de licence) et votre rattachement club. Si le problème persiste, contactez l'organisateur du concours.",
      },
    ],
  },
  {
    title: 'Clubs',
    questions: [
      {
        q: 'Quelles sont les formules pour les clubs ?',
        a: (
          <span>
            Nous proposons une formule 'Club Free' (0€) pour la visibilité et une formule 'Club Pro' (20€/mois) incluant
            la gestion avancée des membres. Retrouvez le comparatif sur notre{' '}
            <Link to={ROUTES.TARIFS} className='text-blue-600 font-bold hover:underline'>
              page Tarifs
            </Link>
            .
          </span>
        ),
      },
      {
        q: 'Comment rejoindre un club sur BoulesConnect ?',
        a: 'Allez sur la page du club (annuaire), puis cliquez sur « Rejoindre ce club ». Vous pouvez ajouter un message (facultatif). Votre demande est ensuite examinée par les administrateurs du club.',
      },
      {
        q: 'Pourquoi je ne peux pas rejoindre un club ?',
        a: "Pour envoyer une demande d'adhésion, vous devez être connecté avec un compte Joueur. Si vous êtes déjà membre d'un club, vous devez d'abord quitter votre club actuel pour pouvoir en rejoindre un autre. Si vous avez déjà une demande en attente, vous pouvez attendre la réponse du club.",
      },
      {
        q: "Comment valider une demande d'adhésion ?",
        a: "Lorsqu'un joueur demande à rejoindre votre club, vous recevez une notification. Rendez-vous dans votre tableau de bord Club, section 'Membres' pour valider ou refuser la demande.",
      },
      {
        q: 'Puis-je exporter la liste de mes membres ?',
        a: 'Oui, les administrateurs de club (formule Pro) peuvent exporter la liste complète des membres avec leurs informations fédérales et coordonnées au format CSV.',
      },
      {
        q: "Comment récupérer la gestion d'un club déjà existant ?",
        a: 'Si votre club est déjà référencé, utilisez le bouton « Revendiquer ce club ». Un processus de vérification est ensuite lancé pour sécuriser la demande. Si vous êtes bloqué, contactez le support avec le nom du club et votre email de compte.',
      },
      {
        q: 'Comment contacter un club (message) ?',
        a: 'Sur la page du club, ouvrez la section « Coordonnées » puis cliquez sur « Envoyer un message ». Vous devez être connecté.',
      },
    ],
  },
  {
    title: 'Organisateurs & Tarifs',
    questions: [
      {
        q: "Combien coûte l'organisation d'un concours ?",
        a: (
          <span>
            Nous proposons deux modèles : la "Commission Partenaire" (0 € fixe + 2 % + 0,30 € par inscription, min 0,75
            €) ou le "Forfait" (80 € TTC/mois ou 800 € TTC/an). Retrouvez tous les détails sur notre{' '}
            <Link to={ROUTES.TARIFS} className='text-blue-600 font-bold hover:underline'>
              page Tarifs
            </Link>
            .
          </span>
        ),
      },
      {
        q: 'Comment fonctionne Stripe Connect ?',
        a: 'Pour encaisser les inscriptions, vous devez connecter votre compte Stripe (ou en créer un gratuitement) via BoulesConnect. Les paiements sont traités par Stripe et reversés sur votre compte bancaire (déduction faite de la commission éventuelle).',
      },
      {
        q: 'La saisie des résultats est-elle obligatoire ?',
        a: "Elle est fortement recommandée pour animer la communauté. Si aucun résultat n'est saisi 48h après la fin du concours, vous recevrez un rappel automatique.",
      },
    ],
  },
  {
    title: 'FFPJP & Fédération',
    questions: [
      {
        q: "Les catégories d'âge sont-elles gérées ?",
        a: "BoulesConnect peut calculer des catégories d'âge à partir de la date de naissance (dans une logique de compatibilité « logiciel fédéral »). Les règles officielles restent celles appliquées par la fédération et/ou le logiciel fédéral le jour J.",
      },
      {
        q: 'Puis-je exporter les inscrits pour le logiciel fédéral ?',
        a: "BoulesConnect propose un export au format XLSX pensé pour faciliter l'import dans un logiciel de gestion de concours. La compatibilité peut évoluer selon les versions/outils utilisés : nous recommandons de vérifier le fichier exporté avant le jour J.",
      },
      {
        q: 'BoulesConnect vérifie-t-il les licences FFPJP ?',
        a: "Non. BoulesConnect ne dispose pas d'un accès officiel au référentiel complet des licences FFPJP et ne vérifie pas automatiquement la validité, le renouvellement ou l'absence de suspension d'une licence. Pour les concours réservés aux licenciés, le joueur renseigne son numéro de licence et déclare sur l'honneur être titulaire d'une licence valide et non suspendue.",
      },
    ],
  },
  {
    title: 'Paiements & Sécurité',
    questions: [
      {
        q: 'Les paiements sont-ils sécurisés ?',
        a: 'Tous les paiements sont traités par Stripe, leader mondial du paiement en ligne. BoulesConnect ne stocke jamais vos informations de carte bancaire.',
      },
      {
        q: 'Comment obtenir un remboursement ?',
        a: "Les remboursements dépendent de la politique de l'organisateur. En pratique : contactez d'abord l'organisateur du concours. Si besoin, le support BoulesConnect peut aider à transmettre, mais ne peut pas se substituer aux conditions de l'organisateur.",
      },
      {
        q: 'Mes informations de carte bancaire sont-elles stockées ?',
        a: 'Non. Les informations de carte bancaire ne transitent pas et ne sont pas stockées par BoulesConnect : elles sont gérées directement par Stripe.',
      },
    ],
  },
];

export default function Support() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const filteredFAQ = useMemo(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    return faqSections
      .map(section => ({
        ...section,
        questions: section.questions.filter(q => {
          if (!searchLower) return true;
          const answerText = String(q.a ?? '');
          return q.q.toLowerCase().includes(searchLower) || answerText.toLowerCase().includes(searchLower);
        }),
      }))
      .filter(
        section =>
          section.questions.length > 0 &&
          (activeCategory === 'all' || section.title.toLowerCase().includes(activeCategory.toLowerCase())),
      );
  }, [searchQuery, activeCategory]);
  return (
    <>
      <PageMeta title='Support' description="Centre d'aide BoulesConnect" />
      <section className='pt-32 pb-16 px-6 bg-linear-to-br from-blue-600 via-indigo-600 to-purple-700 relative overflow-hidden'>
        <div className='absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]' />
        <div className='max-w-4xl mx-auto text-center relative z-10'>
          <span className='inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 px-4 py-2 rounded-full mb-8'>
            <Headphones className='w-4 h-4 text-white' />
            <span className='text-xs font-bold text-white uppercase tracking-wider'>Centre d'aide</span>
          </span>
          <h1 className='text-4xl md:text-5xl font-extrabold text-white mb-6'>Comment pouvons-nous vous aider ?</h1>
          <p className='text-blue-100 max-w-2xl mx-auto mb-8 text-sm md:text-base'>
            Trouvez une réponse en quelques secondes (concours, clubs, inscriptions, paiements). Si vous ne trouvez pas,
            vous pouvez nous contacter.
          </p>
          <div className='relative max-w-xl mx-auto'>
            <Search className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
            <input
              type='text'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={`Ex : "créer un concours", "revendiquer un club", "remboursement"...`}
              className='w-full pl-12 pr-6 py-4 rounded-2xl bg-white text-slate-900 placeholder-slate-400 text-sm font-medium shadow-2xl focus:outline-none focus:ring-4 focus:ring-white/30'
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className='px-6 -mt-8 relative z-10 mb-16'>
        <div className='max-w-6xl mx-auto'>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4'>
            {helpCategories.map(cat => (
              <button
                key={cat.label}
                onClick={() => {
                  setActiveCategory(cat.label);
                  setSearchQuery('');
                }}
                className={`bg-white rounded-2xl border ${activeCategory === cat.label ? 'border-blue-500 shadow-md ring-2 ring-blue-50' : cat.border} p-5 text-center hover:shadow-lg transition-all group cursor-pointer`}
              >
                <div className={`w-10 h-10 rounded-xl ${cat.color} flex items-center justify-center mb-3 mx-auto`}>
                  <cat.icon className='w-5 h-5' />
                </div>
                <h3 className='text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors'>
                  {cat.label}
                </h3>
              </button>
            ))}
          </div>
        </div>
        {activeCategory !== 'all' && (
          <div className='w-full flex justify-center items-center gap-4 mt-5'>
            <button
              onClick={() => {
                setSearchQuery('');
                setActiveCategory('all');
              }}
              className='text-blue-600 font-bold hover:underline'
            >
              Réinitialiser le filtre
            </button>
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className='px-6 mb-20'>
        <div className='max-w-4xl mx-auto'>
          <h2 className='text-3xl font-extrabold text-slate-900 text-center mb-12'>
            {searchQuery || activeCategory !== 'all' ? 'Résultats' : 'Questions fréquentes'}
          </h2>
          <div className='space-y-10'>
            {filteredFAQ.length === 0 ? (
              <div className='text-center py-12'>
                <p className='text-slate-500 mb-4'>Aucun résultat pour cette recherche ou catégorie.</p>
                <div className='flex flex-col sm:flex-row items-center justify-center gap-3'>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('all');
                    }}
                    className='inline-flex items-center gap-2 px-6 py-3 rounded-xl font-extrabold bg-slate-50 hover:bg-slate-100 text-slate-900 border border-slate-200 transition'
                  >
                    Réinitialiser la recherche
                  </button>
                  <Link
                    to={ROUTES.CONTACT}
                    className='inline-flex items-center gap-2 px-6 py-3 rounded-xl font-extrabold bg-blue-600 hover:bg-blue-700 text-white transition shadow-lg shadow-blue-200'
                  >
                    Contacter le support
                  </Link>
                </div>
              </div>
            ) : (
              filteredFAQ.map(section => (
                <div key={section.title}>
                  <h3 className='text-lg font-bold text-slate-900 mb-4 flex items-center gap-2'>
                    <span className='w-1.5 h-6 bg-blue-600 rounded-full' />
                    {section.title}
                  </h3>
                  <div className='space-y-2'>
                    {section.questions.map(faq => (
                      <details
                        key={faq.q}
                        className='group bg-white rounded-2xl border border-slate-100 overflow-hidden'
                      >
                        <summary className='flex items-center justify-between px-6 py-4 cursor-pointer list-none hover:bg-slate-50 transition-colors'>
                          <span className='text-sm font-semibold text-slate-900 pr-4'>{faq.q}</span>
                          <ChevronRight className='w-4 h-4 text-slate-400 shrink-0 transition-transform group-open:rotate-90' />
                        </summary>
                        <div className='px-6 pb-4'>
                          <div className='text-sm text-slate-600 leading-relaxed'>{faq.a}</div>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className='px-6 pb-20'>
        <div className='max-w-4xl mx-auto'>
          <div className='bg-linear-to-r from-blue-600 via-indigo-600 to-purple-700 rounded-3xl p-8 md:p-12 text-center'>
            <h2 className='text-2xl md:text-3xl font-extrabold text-white mb-3'>
              Vous n'avez pas trouvé votre réponse ?
            </h2>
            <p className='text-blue-100 mb-8 max-w-lg mx-auto'>
              Notre équipe de support est disponible du lundi au vendredi de 9h à 18h pour vous aider.
            </p>
            <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Link
                to={ROUTES.CONTACT}
                className='inline-flex items-center gap-2 px-8 py-3.5 bg-white text-blue-700 rounded-2xl font-bold text-sm hover:bg-blue-50 transition-colors'
              >
                Contacter le support
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
